import React, { useState, useEffect, useRef } from 'react';
import Papa from 'papaparse';
import { auth, db } from '../../firebase';
import { getDatabase, ref, set } from 'firebase/database';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearchPlus, faSearchMinus, faSave, faEraser, faRedo, faEllipsis, faSlash, faStop, faPlus, faTimes, faArrowsUpDownLeftRight, faArrowsToDot, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { FiInfo } from 'react-icons/fi';
import Loading from './loading';
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import Style from '../assets/css/draw2.module.css';

const Draw2 = () => {
    const [nombrePlaneta, setNombrePlaneta] = useState('');
    const [plNames, setPlNames] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [error, setError] = useState(null);
    const [showOptions, setShowOptions] = useState(false);
    const [isDrawing, setIsDrawing] = useState(false);
    const [drawEnabled, setDrawEnabled] = useState(false);
    const [strokeColor, setStrokeColor] = useState('#FFFF00');
    const [drawings, setDrawings] = useState([]);
    const [selectedPlanet, setSelectedPlanet] = useState('');
    const [user, setUser] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [isFooterVisible, setFooterVisible] = useState(false);
    const [isFooterAnimating, setFooterAnimating] = useState(false);
    const [imgPosition, setImgPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [draggingEnabled, setDraggingEnabled] = useState(false);
    const canvasRef = useRef(null);
    const selectContainerRef = useRef(null);
    const imgRef = useRef(new Image());
    const { t } = useTranslation();

    const steps = [
        {
            element: `.${Style.drawTitle}`,
            popover: {
                title: t('titleDraw'),
                description: t('titleDrawDescription'),
                position: 'bottom',
            },
        },
        {
            element: `.${Style.drawInput}`,
            popover: {
                title: t('searchDraw'),
                description: t('searchDrawDescription'),
                position: 'bottom',
            },
        },
        {
            element: `.${Style.infoIcon}`,
            popover: {
                title: t('infoDraw'),
                description: t('infoDrawDescription'),
                position: 'left',
            },
        },
        {
            element: `.${Style.buttonsGenerators}`,
            popover: {
                title: t('buttonsDraw'),
                description: t('buttonsDrawDescription'),
                position: 'bottom',
            },
        },
        {
            element: `.${Style.zoomButtons}`,
            popover: {
                title: t('zoomDraw'),
                description: t('zoomDrawDescription'),
                position: 'left',
            },
        },
        {
            element: `.${Style.floatingButton}`,
            popover: {
                title: t('footerDraw'),
                description: t('footerDrawDescription'),
                position: 'left',
            },
        },
        {
            element: `.${Style.canvas}`,
            popover: {
                title: t('canvasDraw'),
                description: t('canvasDrawDescription'),
                position: 'top',
            },
        }
    ];

    // Obtener el parámetro 'planeta' de la URL
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const planetaParam = searchParams.get('planeta');

        if (planetaParam) {
            setInputValue(decodeURIComponent(planetaParam));
        }
    }, [location.search]);

    const startTutorial = () => {
        const driverObj = driver({
            animate: true,
            opacity: 0.75,
            doneBtnText: t('entendidoDraw'),
            closeBtnText: t('closeDraw'),
            nextBtnText: t('nextDraw'),
            prevBtnText: t('prevDraw'),
            steps: steps
        });
        driverObj.drive();
    };

    useEffect(() => {
        const loadCSV = async () => {
            try {
                const response = await fetch('planetas.csv');
                const text = await response.text();
                Papa.parse(text, {
                    header: true,
                    complete: (results) => {
                        const names = results.data.map(row => row.pl_name).filter(name => name);
                        setPlNames(names);
                    },
                    error: (error) => {
                        console.error('Error loading CSV:', error);
                    }
                });
            } catch (error) {
                console.error('Error loading CSV:', error);
            }
        };

        const unsubscribe = auth.onAuthStateChanged((currentUser) => {
            setUser(currentUser);
        });

        loadCSV();

        return () => {
            unsubscribe();
        };
    }, []);

    const filteredNames = plNames.filter(name =>
        name.toLowerCase().includes(inputValue.toLowerCase())
    );

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
        setShowOptions(true);
    };

    const handleOptionClick = (name) => {
        setInputValue(name);
        setSelectedPlanet(name);
        setShowOptions(false);
    };

    const handleZoom = (zoomFactor) => {
        setZoom(prevZoom => {
            const newZoom = Math.max(1, Math.min(prevZoom + zoomFactor, 2));
            if (newZoom === 1) {
                setImgPosition({ x: 0, y: 0 });
            }
            return newZoom;
        });
    };

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.save();
        const imgX = zoom === 1 ? 0 : imgPosition.x;
        const imgY = zoom === 1 ? 0 : imgPosition.y;
        ctx.scale(zoom, zoom);
        ctx.drawImage(imgRef.current, imgX, imgY, canvas.width, canvas.height);
        drawings.forEach(drawing => {
            ctx.strokeStyle = drawing.color;
            ctx.beginPath();
            drawing.points.forEach((point, index) => {
                if (index === 0) {
                    ctx.moveTo(point.x, point.y);
                } else {
                    ctx.lineTo(point.x, point.y);
                }
            });
            ctx.stroke();
        });

        ctx.restore();
    }, [zoom, drawings, imgPosition]);

    const startDragging = (e) => {
        if (!draggingEnabled) return;
        const { x, y } = getMousePosition(e);
        if (x >= imgPosition.x && x <= imgPosition.x + canvasRef.current.width &&
            y >= imgPosition.y && y <= imgPosition.y + canvasRef.current.height) {
            setIsDragging(true);
        }
    };

    const dragImage = (e) => {
        if (isDragging) {
            const { x, y } = getMousePosition(e);
            setImgPosition({ x: x - canvasRef.current.width / 2, y: y - canvasRef.current.height / 2 });
        }
    };

    const stopDragging = () => {
        setIsDragging(false);
    };

    useEffect(() => {
        const canvas = canvasRef.current;
        canvas.addEventListener('mousedown', startDragging);
        canvas.addEventListener('mousemove', dragImage);
        canvas.addEventListener('mouseup', stopDragging);
        canvas.addEventListener('mouseleave', stopDragging);

        canvas.addEventListener('touchstart', (e) => {
            e.preventDefault();
            startDragging(e);
        });
        canvas.addEventListener('touchmove', dragImage);
        canvas.addEventListener('touchend', stopDragging);

        return () => {
            canvas.removeEventListener('mousedown', startDragging);
            canvas.removeEventListener('mousemove', dragImage);
            canvas.removeEventListener('mouseup', stopDragging);
            canvas.removeEventListener('mouseleave', stopDragging);
            canvas.removeEventListener('touchstart', startDragging);
            canvas.removeEventListener('touchmove', dragImage);
            canvas.removeEventListener('touchend', stopDragging);
        };
    }, [isDragging, draggingEnabled]);

    const handleDragToggle = () => {
        if (zoom > 1) {
            setDraggingEnabled(prev => !prev);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (selectContainerRef.current && !selectContainerRef.current.contains(event.target)) {
                setShowOptions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const getMousePosition = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();

        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;

        return {
            x: (clientX - rect.left) * (canvas.width / rect.width) / zoom,
            y: (clientY - rect.top) * (canvas.height / rect.height) / zoom,
        };
    };

    const startDrawing = (e) => {
        if (!drawEnabled) return;
        const { x, y } = getMousePosition(e);
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        ctx.beginPath();
        ctx.setLineDash([1, 5]);
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = 5;
        ctx.moveTo(x, y);
        setIsDrawing(true);

        setDrawings(prev => [...prev, { color: strokeColor, points: [{ x, y }] }]);
    };

    const draw = (e) => {
        if (!isDrawing || !drawEnabled) return;

        const { x, y } = getMousePosition(e);
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        ctx.strokeStyle = strokeColor;
        ctx.lineTo(x, y);
        ctx.stroke();

        setDrawings(prev => {
            const newDrawings = [...prev];
            newDrawings[newDrawings.length - 1].points.push({ x, y });
            return newDrawings;
        });
    };

    const stopDrawing = () => {
        setIsDrawing(false);
    };

    useEffect(() => {
        const canvas = canvasRef.current;
        canvas.addEventListener('mousedown', startDrawing);
        canvas.addEventListener('mousemove', draw);
        canvas.addEventListener('mouseup', stopDrawing);
        canvas.addEventListener('mouseleave', stopDrawing);

        canvas.addEventListener('touchstart', (e) => {
            e.preventDefault();
            startDrawing(e);
        });
        canvas.addEventListener('touchmove', draw);
        canvas.addEventListener('touchend', stopDrawing);

        return () => {
            canvas.removeEventListener('mousedown', startDrawing);
            canvas.removeEventListener('mousemove', draw);
            canvas.removeEventListener('mouseup', stopDrawing);
            canvas.removeEventListener('mouseleave', stopDrawing);
            canvas.removeEventListener('touchstart', startDrawing);
            canvas.removeEventListener('touchmove', draw);
            canvas.removeEventListener('touchend', stopDrawing);
        };
    }, [isDrawing, drawEnabled]);

    const handleClear = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(imgRef.current, 0, 0, canvas.width, canvas.height);
        setDrawings([]);
    };

    const handleSave = async () => {
        const { value: formValues } = await Swal.fire({
            title: t('imageNamePlaceholder'),
            html:
                `<input id="swal-input1" class="swal2-input" placeholder="${t('imageNamePlaceholder')}">` +
                `<textarea id="swal-input2" style="resize: vertical;" class="swal2-textarea" placeholder="${t('story')}"></textarea>`,
            focusConfirm: false,
            showCancelButton: true,
            preConfirm: () => {
                const imageName = document.getElementById('swal-input1').value;
                const storyText = document.getElementById('swal-input2').value;
                if (!imageName || !storyText) {
                Swal.showValidationMessage(t('imageNameRequired'));
                return false; 
                }
                
                return { imageName, storyText };
            }
        });

         if (!formValues) return;

        const { imageName, storyText } = formValues;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        // Crear la imagen original en formato Data URL
        const originalDataURL = canvas.toDataURL();

        // Crear un nuevo canvas para la descarga
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');

        const originalImage = new Image();
        originalImage.src = originalDataURL;

        originalImage.onload = () => {
            // Establecer el tamaño del canvas temporal
            tempCanvas.width = originalImage.width;
            tempCanvas.height = originalImage.height + 150; // Aumentamos el alto para el espacio adicional

            // Limpiar el canvas temporal
            tempCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);

            // Dibujar la imagen original en el canvas temporal
            tempCtx.drawImage(originalImage, 0, 0);

            // Si hay un nombre de planeta, lo dibujamos dentro de la imagen
            if (selectedPlanet) {
                tempCtx.font = '20px Arial';
                tempCtx.fillStyle = 'white';
                tempCtx.textAlign = 'right';
                tempCtx.fillText(selectedPlanet, tempCanvas.width - 10, tempCanvas.height - 10);
            }

            // Cargar la imagen del primer logo
            const logoImage = new Image();
            logoImage.src = 'QR.jpeg';

            logoImage.onload = () => {
                // Definir la posición del primer logo
                const logoX = tempCanvas.width / 2 + 150;
                const logoY = tempCanvas.height - 150;

                // Dibujar el primer logo en el canvas temporal
                tempCtx.drawImage(logoImage, logoX, logoY, 150, 150);

                // Cargar la segunda imagen
                const secondLogoImage = new Image();
                secondLogoImage.src = 'AndiviNundoo.webp';

                secondLogoImage.onload = () => {
                    // Definir la posición de la segunda imagen
                    const secondLogoX = tempCanvas.width / 2 - 200;
                    const secondLogoY = tempCanvas.height - 150;

                    // Dibujar la segunda imagen en el canvas temporal
                    tempCtx.drawImage(secondLogoImage, secondLogoX, secondLogoY, 150, 150);

                    // Generar la nueva imagen con ambos logos
                    const newDataURL = tempCanvas.toDataURL();

                    // Crear un enlace para descargar la nueva imagen
                    const downloadLink = document.createElement('a');
                    downloadLink.href = newDataURL;
                    downloadLink.download = `${imageName}.png`;
                    downloadLink.click();
                };
            };
        };

        // Si el usuario está logueado, guardamos la imagen en la base de datos
        if (user) {
            try {
                const db = getDatabase();  // Obtener la instancia de la base de datos
                const imageRef = ref(db, `images/${user.uid}/${imageName}`);

                // Limpiar el canvas principal antes de guardar la imagen base
                ctx.clearRect(0, 0, canvas.width, canvas.height);

                // Si hay un nombre de planeta, dibujamos dentro de la imagen
                if (selectedPlanet) {
                    const originalImage = new Image();
                    originalImage.src = originalDataURL;
                    originalImage.onload = () => {
                        const newCanvasWidth = canvas.width;  // El tamaño del canvas no cambia
                        const newCanvasHeight = canvas.height;  // Mantenemos la altura original

                        // Redimensionar el canvas si es necesario
                        canvas.width = newCanvasWidth;
                        canvas.height = newCanvasHeight;

                        // Limpiar el canvas
                        ctx.clearRect(0, 0, newCanvasWidth, newCanvasHeight);

                        // Dibujar la imagen original dentro del canvas
                        ctx.drawImage(originalImage, 0, 0);

                        // Dibujar el nombre del planeta dentro de la imagen
                        ctx.font = '20px Arial';
                        ctx.fillStyle = 'white';
                        ctx.textAlign = 'right';
                        ctx.fillText(selectedPlanet, canvas.width - 10, canvas.height - 10); // Asegurarse de que esté dentro

                        // Generar la nueva imagen con el planeta
                        const newDataURL = canvas.toDataURL();

                        // Guardar en Firebase
                        set(imageRef, {
                            imageName,
                            dataURL: newDataURL,  // Guardar la imagen con el nombre del planeta
                            storyText,  // Guardar la historia en la base de datos
                            selectedPlanet,  // Guardar el nombre del planeta
                            timestamp: new Date().toISOString()
                        });

                        Swal.fire(t('success'), t('imageSavedWithStory'), 'success');
                    };
                } else {
                    // Si no hay planeta, guardar la imagen original
                    set(imageRef, {
                        imageName,
                        dataURL: originalDataURL,  // Guardar la imagen original sin el nombre del planeta
                        storyText,
                        timestamp: new Date().toISOString()
                    });

                    Swal.fire(t('success'), t('imageSavedWithStory'), 'success');
                }
            } catch (error) {
                Swal.fire(t('error'), t('failedToSaveImage'), 'error');
            }
        } else {
            Swal.fire(t('warning'), t('needToBeLoggedIn'), 'warning');
        }
    };

    const handleSearch = async () => {
        if (!inputValue) {
            Swal.fire({
                icon: 'warning',
                text: t('pleaseEnterPlanetName'),
            });
            return;
        }
        
        try {
            
            Swal.fire({
            text: t('loadingStarMap'),
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            },
            });

            const encodedName = encodeURIComponent(inputValue.trim());
            const response = await fetch(`https://nasa-api-zfsj.onrender.com/draw2D/api/mapa_estelar?planeta=${encodedName}`);

            if (!response.ok) {
                throw new Error(t('errorFetchingStarMap'));
            }

            const blob = await response.blob();
            const imageUrl = URL.createObjectURL(blob);
            imgRef.current.src = imageUrl;

            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');

            const img = imgRef.current;

            img.onload = () => {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                 Swal.close()
            };

            img.src = imageUrl;
        } catch (error) {
            Swal.fire(t('errorFetchingStarMapRetry'));
        }
    };

    const handleInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleDrawToggle = () => {
        setDrawEnabled(prev => !prev);
    };

    const handleColorChange = (e) => {
        setStrokeColor(e.target.value);
    };

    const handleErase = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(imgRef.current, 0, 0, canvas.width, canvas.height);
        setDrawings(prev => {
            const newDrawings = prev.slice(0, -1);
            newDrawings.forEach(drawing => {
                ctx.strokeStyle = drawing.color;
                ctx.beginPath();
                drawing.points.forEach((point, index) => {
                    if (index === 0) ctx.moveTo(point.x, point.y);
                    else ctx.lineTo(point.x, point.y);
                });
                ctx.stroke();
            });
            return newDrawings;
        });
    };

    const toggleFooter = () => {
        if (isFooterVisible) {
            setFooterAnimating(true);
            setTimeout(() => {
                setFooterVisible(false);
                setFooterAnimating(false);
            }, 300);
        } else {
            setFooterVisible(true);
            setFooterAnimating(false);
        }
    };

    return (
        <>
            <section className={Style.drawContainer}>
                <div className={Style.drawTitleContainer}>
                    <h1 className={Style.drawTitle}>{t('drawTitle')}</h1>
                </div>
                <div className={Style.selectContainer} ref={selectContainerRef}>
                    <div className={Style.inputAndAlert}>
                        <input
                            type="text"
                            value={inputValue}
                            onChange={handleInputChange}
                            onClick={() => setShowOptions(true)}
                            onKeyDown={handleInputKeyDown}
                            placeholder={t('planetName')}
                            className={Style.drawInput}
                        />
                        <FiInfo
                            className={Style.infoIcon}
                            onClick={() => Swal.fire({
                                icon: 'warning',
                                text: t('infoMessage')
                            })}
                        />
                    </div>
                    <div className={Style.buttonsGenerators}>
                        <Link
                            to={`/draw3D?planeta=${encodeURIComponent(inputValue)}`}
                            className={Style.linkButton}
                        >
                            {t('drawButton3D')}
                        </Link>
                        <button type="submit" onClick={handleSearch} className={Style.drawButton}>{t('show2D')}</button>
                        <button onClick={startTutorial} className={Style.tutorialButton}>
                            <FontAwesomeIcon icon={faQuestionCircle} /> {t('tutorialButton')}
                        </button>
                    </div>

                    {showOptions && (
                        <ul className={Style.dropdown}>
                            {filteredNames.length > 0 ? (
                                filteredNames.map((name, index) => (
                                    <li
                                        key={index}
                                        onClick={() => handleOptionClick(name)}
                                        className={Style.dropdownItem}
                                    >
                                        {name}
                                    </li>
                                ))
                            ) : (
                                inputValue && <li className={Style.dropdownItem}>No planets found</li>
                            )}
                        </ul>
                    )}
                </div>
                {error && <p className={Style.error}>{error}</p>}
                <div className={Style.canvasContainer}>
                    <div className={Style.zoomButtons}>
                        <button onClick={() => handleZoom(0.1)}><FontAwesomeIcon icon={faSearchPlus} /></button>
                        <button onClick={() => handleZoom(-0.1)}><FontAwesomeIcon icon={faSearchMinus} /></button>
                        <button
                            onClick={handleDragToggle}
                            className={`${Style.button} ${zoom <= 1 ? Style.disabled : ''}`}
                            disabled={zoom <= 1}
                        >
                            {draggingEnabled ? <FontAwesomeIcon icon={faArrowsToDot} /> : <FontAwesomeIcon icon={faArrowsUpDownLeftRight} />}
                        </button>
                    </div>
                    <canvas ref={canvasRef} className={Style.canvas} width={900} height={500} />
                </div>
                <div className={Style.floatingButtonContainer}>
                    <button onClick={toggleFooter} className={Style.floatingButton}>
                        <FontAwesomeIcon icon={isFooterVisible ? faTimes : faPlus} />
                    </button>
                </div>
                {isFooterVisible && (
                    <footer className={`${Style.footerTools} ${isFooterAnimating ? Style.hidden : ''}`}>
                        <button onClick={handleDrawToggle} className={Style.button}>
                            {drawEnabled ? <FontAwesomeIcon icon={faStop} /> : <FontAwesomeIcon className={Style.iconBounce} icon={faEllipsis} />}
                        </button>
                        <div className={Style.separator}></div>
                        <button onClick={handleErase} className={Style.button}><FontAwesomeIcon className={Style.fadeInOut} icon={faEraser} /></button>
                        <div className={Style.separator}></div>
                        <button onClick={handleSave} className={Style.button}><FontAwesomeIcon className={Style.scaleUpDown} icon={faSave} /></button>
                        <div className={Style.separator}></div>
                        <input
                            type="color"
                            id="colorPicker"
                            value={strokeColor}
                            onChange={handleColorChange}
                        />
                        <div className={Style.separator}></div>
                        <button onClick={handleClear} className={Style.button}><FontAwesomeIcon className={Style.rotateLeftRight} icon={faRedo} /></button>
                    </footer>
                )}
            </section>
        </>
    );
};

export default Draw2;
